import { ElevationProfile } from '@/atoms/elevationProfile.atom';
import { buildSegments, chainageToWorld } from './polylineMath';

/** Shared options for both CSV and GeoJSON exports. */
export interface ProfileExportOptions {
  /** Optional human-readable layer name, included in CSV header / GeoJSON properties. */
  sourceLayerName?: string;
  /**
   * Project SRID for the GeoJSON CRS field. Omitted (or `-1`) skips the
   * `crs` property — matches `measurementToGeoJSON`'s behavior in
   * `Layers.tsx` so the unset-SRID path produces the same output.
   */
  srid?: number;
}

/**
 * Build the CSV body for an elevation profile.
 *
 * Columns: `chainage_m, x, y, z`. Chainage is the distance along the
 * polyline from the first vertex (XY only, since Z is the elevation we're
 * plotting). X / Y / Z are world coordinates in the project SRID; X and Y
 * are derived from the polyline at each sample's chainage (so the row is
 * the corridor's centerline at that distance) and Z is the sampled
 * elevation value.
 *
 * Why corridor-centerline XY rather than the original raw points' XY?
 * After the per-point projection in `computeElevationProfile`, all we
 * keep per sample is `{chainage, elevation}` — the raw XY was discarded
 * (and would be ambiguous anyway, since multiple raw points within the
 * buffer can collapse to the same chainage). Reconstructing XY from the
 * polyline gives a deterministic 3-D path that traces the profile.
 *
 * Header lines (prefixed with `#`) carry source/parameter context so the
 * file is self-descriptive. Most CSV readers (Excel, pandas, QGIS) skip
 * `#` comment rows or treat them as text in the first column with no
 * downstream effect.
 */
export function profileToCsv(profile: ElevationProfile, options: ProfileExportOptions = {}): string {
  const segments = buildSegments(profile.polylinePoints);
  const lines: string[] = [];
  lines.push(`# ${profile.name || 'Elevation profile'}`);
  if (options.sourceLayerName) {
    lines.push(`# Source layer: ${options.sourceLayerName}`);
  }
  lines.push(`# Buffer: ${profile.bufferDistance.toFixed(2)} m`);
  if (profile.totalLength > 0) {
    lines.push(`# Total length: ${profile.totalLength.toFixed(2)} m`);
  }
  lines.push(`# Samples: ${profile.samples.length}`);
  lines.push('chainage_m,x,y,z');

  for (const s of profile.samples) {
    const w = chainageToWorld(s.chainage, segments);
    // Fall back to NaN-as-empty if chainageToWorld returns null (degenerate
    // polyline). Shouldn't happen in practice — samples only exist when
    // segments exist — but cheap defensive guard.
    const x = w?.x;
    const y = w?.y;
    const xs = x != null ? x.toFixed(4) : '';
    const ys = y != null ? y.toFixed(4) : '';
    lines.push(`${s.chainage.toFixed(4)},${xs},${ys},${s.elevation.toFixed(4)}`);
  }
  return lines.join('\n');
}

interface GeoJsonLineFeature {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: { type: 'LineString'; coordinates: number[][] };
}

interface GeoJsonFeatureCollection {
  type: 'FeatureCollection';
  crs?: { type: 'name'; properties: { name: string } };
  features: GeoJsonLineFeature[];
}

/**
 * Build a GeoJSON FeatureCollection for an elevation profile.
 *
 * Two LineString features:
 *   - `kind: "polyline"`  — the user-drawn polyline (vertex coordinates),
 *     intended for re-visualizing the corridor centerline in another tool.
 *   - `kind: "sampledProfile"` — the corridor traced through the sampled
 *     elevations: XY from `chainageToWorld(chainage)`, Z from the sample's
 *     elevation. Carries chainages as a parallel array so consumers can
 *     plot distance-vs-elevation without re-deriving distance.
 *
 * The sampledProfile feature is omitted entirely if fewer than two valid
 * samples — a single-point LineString is invalid GeoJSON. Consumers should
 * check `feature.properties.kind`, not feature index.
 *
 * CRS uses the OGC URN form recognised by QGIS / GDAL — same shape as
 * `measurementToGeoJSON` / `geojsonCrs` in `Layers.tsx`.
 */
export function profileToGeoJson(
  profile: ElevationProfile,
  options: ProfileExportOptions = {},
): GeoJsonFeatureCollection {
  const segments = buildSegments(profile.polylinePoints);
  const features: GeoJsonLineFeature[] = [];

  // Polyline feature — always present (the user drew at least 2 vertices
  // to have created the profile in the first place).
  if (profile.polylinePoints.length >= 2) {
    features.push({
      type: 'Feature',
      properties: {
        kind: 'polyline',
        name: profile.name || 'Elevation profile',
        bufferDistance: profile.bufferDistance,
        sourceLayer: options.sourceLayerName ?? null,
        sourceLayerId: profile.sourceLayerId,
        totalLength: profile.totalLength,
        vertexCount: profile.polylinePoints.length,
        createdAt: profile.createdAt,
      },
      geometry: {
        type: 'LineString',
        coordinates: profile.polylinePoints.map((p) => [p[0], p[1], p[2]]),
      },
    });
  }

  // Sampled-profile feature — corridor centerline traced through the
  // sampled elevations. Skip when we don't have enough samples to form a
  // valid LineString (≥ 2 points).
  const sampledCoords: number[][] = [];
  const chainages: number[] = [];
  for (const s of profile.samples) {
    const w = chainageToWorld(s.chainage, segments);
    if (!w) continue;
    sampledCoords.push([w.x, w.y, s.elevation]);
    chainages.push(s.chainage);
  }
  if (sampledCoords.length >= 2) {
    features.push({
      type: 'Feature',
      properties: {
        kind: 'sampledProfile',
        name: profile.name || 'Elevation profile',
        pointCount: profile.pointCount,
        sampleCount: profile.samples.length,
        chainages,
      },
      geometry: { type: 'LineString', coordinates: sampledCoords },
    });
  }

  const fc: GeoJsonFeatureCollection = { type: 'FeatureCollection', features };
  if (options.srid != null && options.srid !== -1) {
    fc.crs = { type: 'name', properties: { name: `urn:ogc:def:crs:EPSG::${options.srid}` } };
  }
  return fc;
}

